<template>
  <list-page-shell>
    <template #actions>
      <el-button v-permission="['SYSTEM_ROLE_CREATE']" type="primary" size="small" @click="openCreate">{{ $t('systemRolesPage.create') }}</el-button>
      <el-button size="small" plain @click="load">{{ $t('common.refresh') }}</el-button>
    </template>

    <template #filters>
      <el-form inline size="small" class="filter-form">
        <el-form-item :label="$t('common.keyword')">
          <el-input v-model.trim="query.keyword" :placeholder="$t('systemRolesPage.keywordPh')" clearable @keyup.enter.native="handleSearch" />
        </el-form-item>
        <el-form-item :label="$t('common.status')">
          <el-select v-model="query.statusCode" clearable :placeholder="$t('common.all')" style="width: 120px;">
            <el-option :value="1" :label="$t('common.active')" />
            <el-option :value="2" :label="$t('common.inactive')" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="handleSearch">{{ $t('common.search') }}</el-button>
          <el-button @click="handleReset">{{ $t('common.reset') }}</el-button>
        </el-form-item>
      </el-form>
    </template>

    <el-table v-loading="loading" :data="rows" size="small" border>
      <el-table-column prop="roleCode" :label="$t('grid.roleCode')" width="160" />
      <el-table-column prop="roleName" :label="$t('grid.roleName')" min-width="180" />
      <el-table-column prop="description" :label="$t('grid.description')" min-width="220" show-overflow-tooltip />
      <el-table-column prop="userCount" :label="$t('grid.userCount')" width="90" align="center" />
      <el-table-column prop="menuCount" :label="$t('grid.menuCount')" width="90" align="center" />
      <el-table-column :label="$t('common.status')" width="110" align="center">
        <template slot-scope="scope">
          <el-switch
            v-permission="['SYSTEM_ROLE_STATUS']"
            :value="scope.row.statusCode"
            :active-value="1"
            :inactive-value="2"
            @change="toggleStatus(scope.row, $event)"
          />
          <el-tag v-if="!hasPermission('SYSTEM_ROLE_STATUS')" size="mini" :type="scope.row.statusCode === 1 ? 'success' : 'info'">
            {{ scope.row.statusCode === 1 ? $t('common.active') : $t('common.inactive') }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column :label="$t('grid.createdAt')" width="210">
        <template slot-scope="scope">{{ formatDisplayDateTime(scope.row.createdAt) || '—' }}</template>
      </el-table-column>
      <el-table-column :label="$t('common.actions')" width="220" fixed="right">
        <template slot-scope="scope">
          <el-button v-permission="['SYSTEM_ROLE_EDIT']" type="text" size="small" @click="openEdit(scope.row)">{{ $t('common.edit') }}</el-button>
          <el-button v-permission="['SYSTEM_ROLE_AUTHORIZE']" type="text" size="small" @click="openPermissionDialog(scope.row)">{{ $t('systemRolesPage.assignPerm') }}</el-button>
          <el-button v-permission="['SYSTEM_ROLE_DELETE']" type="text" size="small" class="danger-text" @click="handleDelete(scope.row)">{{ $t('common.delete') }}</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager">
      <el-pagination
        background
        layout="total, prev, pager, next"
        :total="total"
        :page-size="query.pageSize"
        :current-page.sync="query.pageNo"
        @current-change="load"
      />
    </div>

    <form-dialog :title="editingId ? $t('systemRolesPage.editRole') : $t('systemRolesPage.newRole')" :visible.sync="editDialogVisible" width="520px">
      <el-form ref="roleFormRef" :model="roleForm" :rules="roleRules" label-width="90px">
        <el-form-item :label="$t('grid.roleCode')" prop="roleCode">
          <el-input v-model.trim="roleForm.roleCode" maxlength="64" />
        </el-form-item>
        <el-form-item :label="$t('grid.roleName')" prop="roleName">
          <el-input v-model.trim="roleForm.roleName" maxlength="64" />
        </el-form-item>
        <el-form-item :label="$t('grid.description')">
          <el-input v-model.trim="roleForm.description" type="textarea" :rows="3" maxlength="255" show-word-limit />
        </el-form-item>
        <el-form-item :label="$t('common.status')">
          <el-radio-group v-model="roleForm.statusCode">
            <el-radio :label="1">{{ $t('common.active') }}</el-radio>
            <el-radio :label="2">{{ $t('common.inactive') }}</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="editDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="saveRole">{{ $t('common.save') }}</el-button>
      </span>
    </form-dialog>

    <form-dialog :title="$t('systemRolesPage.permDialogTitle')" :visible.sync="permissionDialogVisible" width="760px">
      <div class="toolbar-row">
        <el-input v-model.trim="menuKeyword" size="small" :placeholder="$t('systemRolesPage.filterMenuPh')" clearable class="menu-filter" />
        <el-button size="small" @click="setAllChecked">{{ $t('common.selectAll') }}</el-button>
        <el-button size="small" @click="clearAllChecked">{{ $t('common.clearAll') }}</el-button>
      </div>
      <el-tree
        ref="menuTree"
        v-loading="menuLoading"
        :data="menuTree"
        show-checkbox
        node-key="id"
        default-expand-all
        :props="treeProps"
        :filter-node-method="filterMenuNode"
        class="menu-tree"
      />
      <span slot="footer">
        <el-button @click="permissionDialogVisible = false">{{ $t('common.cancel') }}</el-button>
        <el-button type="primary" :loading="permissionSaving" @click="savePermissions">{{ $t('common.save') }}</el-button>
      </span>
    </form-dialog>
  </list-page-shell>
</template>

<script>
import ListPageShell from '@/components/ListPageShell.vue'
import FormDialog from '@/components/FormDialog.vue'
import {
  createSystemRole,
  deleteSystemRole,
  getSystemMenuTree,
  getSystemRoleMenuIds,
  getSystemRoles,
  saveSystemRoleMenus,
  updateSystemRole,
  updateSystemRoleStatus
} from '@/api/system'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'

function collectTreeIds(nodes, acc = []) {
  (nodes || []).forEach(item => {
    acc.push(item.id)
    collectTreeIds(item.children || [], acc)
  })
  return acc
}

export default {
  name: 'SystemRoles',
  components: { ListPageShell, FormDialog },
  data() {
    return {
      loading: false,
      saving: false,
      menuLoading: false,
      permissionSaving: false,
      rows: [],
      total: 0,
      query: {
        pageNo: 1,
        pageSize: 10,
        keyword: '',
        statusCode: null
      },
      treeProps: {
        label: 'displayName',
        children: 'children'
      },
      menuTree: [],
      menuKeyword: '',
      editDialogVisible: false,
      permissionDialogVisible: false,
      editingId: null,
      currentRow: null,
      roleForm: {
        roleCode: '',
        roleName: '',
        description: '',
        statusCode: 1
      }
    }
  },
  computed: {
    roleRules() {
      return {
        roleCode: [{ required: true, message: this.$t('systemRolesPage.rulesCode'), trigger: 'blur' }],
        roleName: [{ required: true, message: this.$t('systemRolesPage.rulesName'), trigger: 'blur' }]
      }
    }
  },
  watch: {
    menuKeyword(val) {
      if (this.$refs.menuTree) {
        this.$refs.menuTree.filter(val)
      }
    }
  },
  async created() {
    await this.$store.dispatch('fetchUserInfo')
    await this.load()
  },
  methods: {
    formatDisplayDateTime,
    decorateMenuTree(nodes) {
      return (nodes || []).map(item => ({
        ...item,
        displayName:
          item.menuTypeCode === 2
            ? `${this.$t('systemRolesPage.treePrefixBtn')} ${item.menuName}`
            : item.menuName,
        children: this.decorateMenuTree(item.children || [])
      }))
    },
    hasPermission(code) {
      const permissions = (this.$store.state.userInfo && this.$store.state.userInfo.permissions) || []
      const roleCodes = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
      return permissions.includes(code) || roleCodes.includes(code)
    },
    currentUserHasRole(row) {
      const roleCodes = (this.$store.state.userInfo && this.$store.state.userInfo.roleCodes) || []
      return !!(row && row.roleCode && roleCodes.includes(row.roleCode))
    },
    async load() {
      this.loading = true
      try {
        const resp = await getSystemRoles(this.query)
        const data = resp.data || {}
        this.rows = data.records || []
        this.total = data.total || 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.query.pageNo = 1
      this.load()
    },
    handleReset() {
      this.query = { pageNo: 1, pageSize: 10, keyword: '', statusCode: null }
      this.load()
    },
    resetRoleForm() {
      this.roleForm = {
        roleCode: '',
        roleName: '',
        description: '',
        statusCode: 1
      }
      this.$nextTick(() => {
        if (this.$refs.roleFormRef) this.$refs.roleFormRef.clearValidate()
      })
    },
    openCreate() {
      this.editingId = null
      this.resetRoleForm()
      this.editDialogVisible = true
    },
    openEdit(row) {
      this.editingId = row.id
      this.roleForm = {
        roleCode: row.roleCode || '',
        roleName: row.roleName || '',
        description: row.description || '',
        statusCode: row.statusCode || 1
      }
      this.editDialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.roleFormRef) this.$refs.roleFormRef.clearValidate()
      })
    },
    saveRole() {
      this.$refs.roleFormRef.validate(async valid => {
        if (!valid) return
        this.saving = true
        try {
          if (this.editingId) {
            await updateSystemRole(this.editingId, this.roleForm)
          } else {
            await createSystemRole(this.roleForm)
          }
          this.$message.success(this.editingId ? this.$t('systemRolesPage.updated') : this.$t('systemRolesPage.created'))
          this.editDialogVisible = false
          await this.load()
        } catch (e) {
          this.$message.error((e && e.message) || this.$t('systemUsersPage.saveFailed'))
        } finally {
          this.saving = false
        }
      })
    },
    async toggleStatus(row, value) {
      const previous = row.statusCode
      row.statusCode = value
      try {
        await updateSystemRoleStatus(row.id, value)
        this.$message.success(this.$t('systemUsersPage.statusUpdated'))
        if (this.currentUserHasRole(row)) {
          await this.$store.dispatch('fetchUserInfo')
        }
      } catch (e) {
        row.statusCode = previous
        this.$message.error((e && e.message) || this.$t('systemUsersPage.statusFailed'))
      }
    },
    async openPermissionDialog(row) {
      this.currentRow = row
      this.permissionDialogVisible = true
      this.menuKeyword = ''
      this.menuLoading = true
      try {
        const [treeResp, checkedResp] = await Promise.all([
          getSystemMenuTree(),
          getSystemRoleMenuIds(row.id)
        ])
        this.menuTree = this.decorateMenuTree(treeResp.data || [])
        const checkedIds = checkedResp.data || []
        this.$nextTick(() => {
          if (this.$refs.menuTree) {
            this.$refs.menuTree.setCheckedKeys(checkedIds)
          }
        })
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('systemRolesPage.menuLoadFailed'))
      } finally {
        this.menuLoading = false
      }
    },
    filterMenuNode(value, data) {
      if (!value) return true
      const keyword = String(value).toLowerCase()
      return [data.menuName, data.permissionCode, data.routePath].some(item =>
        String(item || '')
          .toLowerCase()
          .includes(keyword)
      )
    },
    setAllChecked() {
      const ids = collectTreeIds(this.menuTree)
      if (this.$refs.menuTree) {
        this.$refs.menuTree.setCheckedKeys(ids)
      }
    },
    clearAllChecked() {
      if (this.$refs.menuTree) {
        this.$refs.menuTree.setCheckedKeys([])
      }
    },
    async savePermissions() {
      if (!this.currentRow || !this.$refs.menuTree) return
      this.permissionSaving = true
      try {
        const checked = this.$refs.menuTree.getCheckedKeys()
        const halfChecked = this.$refs.menuTree.getHalfCheckedKeys()
        const ids = Array.from(new Set([...(checked || []), ...(halfChecked || [])]))
        await saveSystemRoleMenus(this.currentRow.id, ids)
        this.$message.success(this.$t('systemRolesPage.permSaved'))
        this.permissionDialogVisible = false
        if (this.currentUserHasRole(this.currentRow)) {
          await this.$store.dispatch('fetchUserInfo')
        }
        await this.load()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('systemRolesPage.permSaveFailed'))
      } finally {
        this.permissionSaving = false
      }
    },
    handleDelete(row) {
      this.$confirm(this.$t('systemRolesPage.confirmDelete', { name: row.roleName }), this.$t('common.tip'), { type: 'warning' })
        .then(async () => {
          await deleteSystemRole(row.id)
          this.$message.success(this.$t('systemRolesPage.deleted'))
          if (this.rows.length === 1 && this.query.pageNo > 1) {
            this.query.pageNo -= 1
          }
          await this.load()
        })
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.filter-form {
  display: flex;
  flex-wrap: wrap;
}

.pager {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.danger-text {
  color: #f56c6c;
}

.toolbar-row {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}

.menu-filter {
  max-width: 260px;
}

.menu-tree {
  max-height: 480px;
  overflow: auto;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-surface);
  box-shadow: var(--shadow-card);
}
</style>
